/**
 * Help Articles Hooks (Issue #1348)
 *
 * Phase 1: Returns static data from TypeScript file.
 * Phase 2: Will use useQuery to fetch from /api/content/help
 *
 * Interface matches TanStack Query pattern for seamless API transition.
 */

import { HELP_ARTICLES, HELP_CATEGORIES } from '../data/helpArticles';
import type {
  HelpArticle,
  HelpCategory,
  UseHelpArticlesResult,
  UseHelpArticleResult,
  UseHelpCategoryResult,
} from '../types/help';

/**
 * Hook to fetch all help articles and categories.
 *
 * Use for the help index page.
 */
export function useHelpArticles(): UseHelpArticlesResult {
  return {
    articles: HELP_ARTICLES,
    categories: HELP_CATEGORIES,
    isLoading: false,
    error: null,
  };
}

/**
 * Hook to fetch a single help article by slug.
 *
 * Use for individual article pages.
 */
export function useHelpArticle(slug: string): UseHelpArticleResult {
  const article = HELP_ARTICLES.find((a) => a.slug === slug);
  return {
    article,
    isLoading: false,
    error: null,
  };
}

/**
 * Hook to fetch articles for a specific category.
 *
 * Use for category listing pages.
 */
export function useHelpCategory(category: HelpCategory | string): UseHelpCategoryResult {
  const articles = HELP_ARTICLES.filter((a) => a.category === category);
  const meta = HELP_CATEGORIES.find((c) => c.slug === category);
  return {
    articles,
    meta,
    isLoading: false,
    error: null,
  };
}

/**
 * Hook to get related articles for an article.
 *
 * Returns the next article and any explicitly related articles.
 */
export function useRelatedArticles(article: HelpArticle | undefined): HelpArticle[] {
  if (!article) return [];

  const related: HelpArticle[] = [];

  // Add next article if specified
  if (article.nextArticle) {
    const next = HELP_ARTICLES.find((a) => a.slug === article.nextArticle);
    if (next) related.push(next);
  }

  // Add explicitly related articles
  if (article.relatedArticles) {
    for (const slug of article.relatedArticles) {
      const relatedArticle = HELP_ARTICLES.find((a) => a.slug === slug);
      if (relatedArticle && !related.includes(relatedArticle)) {
        related.push(relatedArticle);
      }
    }
  }

  return related;
}

/**
 * Hook to get public articles (accessible without auth).
 *
 * Use for pre-auth help content like sign-up instructions.
 */
export function usePublicHelpArticles(): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.isPublic === true);
}

// ============================================================================
// Phase 2 (future API-backed implementation):
// ============================================================================
//
// import { useQuery } from '@tanstack/react-query';
// import { apiGet } from '../lib/api';
//
// interface HelpArticlesResponse {
//   articles: HelpArticle[];
//   categories: HelpCategoryMeta[];
// }
//
// export function useHelpArticles() {
//   return useQuery({
//     queryKey: ['cms', 'help-articles'],
//     queryFn: () => apiGet<HelpArticlesResponse>('/api/content/help'),
//     staleTime: 1000 * 60 * 60, // 1 hour
//   });
// }
//
// export function useHelpArticle(slug: string) {
//   return useQuery({
//     queryKey: ['cms', 'help-article', slug],
//     queryFn: () => apiGet<HelpArticle>(`/api/content/help/${slug}`),
//     staleTime: 1000 * 60 * 60,
//   });
// }
